import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getProvince, clearProvinceCache } from '../api/province';
import type { Province } from '../types/province';

interface ProvinceContextType {
  province: Province | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  refetch: () => Promise<void>;
  updateProvince: (province: Province) => void;
}

const ProvinceContext = createContext<ProvinceContextType | null>(null);

export function ProvinceProvider({ children }: { children: ReactNode }) {
  const { provinceId, worldId } = useAuth();
  const location = useLocation();
  const [province, setProvince] = useState<Province | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isInitialMount = useRef(true);
  const fetchInProgressRef = useRef(false);
  const provinceRef = useRef<Province | null>(null);

  const fetchProvince = useCallback(async () => {
    if (!provinceId || !worldId) {
      setIsLoading(false);
      provinceRef.current = null;
      return;
    }

    // Prevent duplicate concurrent fetches (React StrictMode double-mount)
    if (fetchInProgressRef.current) {
      return;
    }

    fetchInProgressRef.current = true;
    // Stale-while-revalidate: only show the spinner on the first load.
    // Background refetches (navigation, tab changes) keep the previous
    // province visible so menu clicks don't flash a loading state.
    if (provinceRef.current === null) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const result = await getProvince(worldId, provinceId);

      if (result.error) {
        setError(result.error);
      } else if (result.province) {
        setProvince(result.province);
        provinceRef.current = result.province;
      }

      setIsLoading(false);
    } finally {
      fetchInProgressRef.current = false;
    }
  }, [provinceId, worldId]);

  useEffect(() => {
    fetchProvince();
  }, [fetchProvince]);

  const refresh = useCallback(async () => {
    // Clear cache to force fresh fetch
    clearProvinceCache();
    await fetchProvince();
  }, [fetchProvince]);

  // Refetch province data (respects ETag cache, allows 304 responses)
  const refetch = useCallback(async () => {
    await fetchProvince();
  }, [fetchProvince]);

  // Auto-refresh province data on navigation (respects ETag cache)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchProvince();
  }, [location.pathname, fetchProvince]);

  // Allow direct province updates from API responses (avoids extra fetch)
  const updateProvince = useCallback((newProvince: Province) => {
    setProvince(newProvince);
    // Also clear the ETag cache so next fetch gets fresh data
    clearProvinceCache();
  }, []);

  return (
    <ProvinceContext.Provider
      value={{
        province,
        isLoading,
        error,
        refresh,
        refetch,
        updateProvince,
      }}
    >
      {children}
    </ProvinceContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useProvince() {
  const context = useContext(ProvinceContext);
  if (!context) {
    throw new Error('useProvince must be used within ProvinceProvider');
  }
  return context;
}
